import { Box } from "@mui/material";
import * as React from "react";
import { useMultiHangarState } from "../../containers/MultiHangarContainer";
import { useRampState } from "../../containers/RampStateContainer";
import { useThisHangar } from "../../hooks/useThisHangar";
import { Preferences } from "../../hooks/useUserPreferences";
import { RampCanvasReadOnlyNew } from "../../screens/Settings/RampsSettings/RampEditor/RampCanvas/RampCanvasReadOnly";
import { RampCanvasReadOnly } from "../../screens/Settings/RampsSettings/RampEditor/RampCanvasReadOnly";
import { Hangar, Location, PlacementWithLocation, Ramp } from "../../types";
import { ZoneHeatmap } from "../../screens/Ramp/TemporalTest/components/ZoneHeatmap";
import { useHeatmapData } from "../../screens/Ramp/TemporalTest/hooks/useHeatmapData";
import { HangarLayoutBackground } from "./HangarLayoutBackground";
import { MeasuringTool } from "./MeasuringTool";

type Props = {
  selectable: boolean;
  location: Location<Hangar | Ramp>;
  preferences: Preferences;
  width: number;
  height: number;
  feetToPixels: number;
  zoomLevel?: number;
  backgroundColor: string;
  dashColor: string;
  borderColor?: string;
  printable?: boolean;
  readOnly?: boolean;
  onClick: (evt: React.MouseEvent<HTMLElement>) => void;
  onMouseDown: (evt: React.MouseEvent<HTMLElement>) => void;
  onMouseUp: (evt: React.MouseEvent<HTMLElement>) => void;
  onMouseMove: (evt: React.MouseEvent<HTMLElement>) => void;
  onMouseEnter: (evt: React.MouseEvent<HTMLElement>) => void;
  onContextMenu: (evt: React.MouseEvent<HTMLElement>) => void;
  children?: React.ReactNode;
  // Heatmap props (for scheduled view)
  heatmapPlacements?: PlacementWithLocation[];
  heatmapSelectedDate?: Date;
};

export const LayoutBackground = React.forwardRef((props: Props, ref) => {
  const {
    selectable,
    location,
    preferences,
    width,
    height,
    feetToPixels,
    zoomLevel,
    onClick,
    onMouseDown,
    onMouseUp,
    onMouseMove,
    onMouseEnter,
    onContextMenu,
    backgroundColor,
    dashColor,
    borderColor,
    printable,
    heatmapPlacements,
    heatmapSelectedDate,
    ...otherProps
  } = props;

  // TODO: not sure wtf is going on here...
  const { getHangar, isMultiHangar } = useMultiHangarState();
  const { hangar: hangarX } = useThisHangar();
  const hangar =
    (isMultiHangar ? getHangar(location.id) : hangarX) || (location as Hangar);
  if (location.type === "hangar") {
    return (
      <MeasuringTool pixelsToFeetRatio={feetToPixels}>
        <HangarLayoutBackground
          ref={ref}
          hangar={hangar}
          selectable={false}
          width={width}
          height={height}
          feetToPixels={feetToPixels}
          preferences={preferences}
          backgroundColor={backgroundColor}
          dashColor={dashColor}
          borderColor={borderColor}
          onClick={onClick}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          onMouseMove={onMouseMove}
          onMouseEnter={onMouseEnter}
          onContextMenu={onContextMenu}
        >
          {props.children}
        </HangarLayoutBackground>
      </MeasuringTool>
    );
  }

  const { ramp } = useRampState();
  const showReferenceImage = preferences?.showRampReferenceImage ?? false;
  const showSpots = preferences?.showSpots ?? false;
  const showHeatmap = preferences?.showZoneHeatmap ?? false;
  const rampBackgroundColor = showReferenceImage
    ? `${props.backgroundColor}80`
    : props.backgroundColor;

  // Calculate heatmap data for scheduled view
  const heatmapData = useHeatmapData(
    heatmapPlacements || [],
    ramp?.shapes || [],
    heatmapSelectedDate || new Date(),
    ramp?.depth || 0,  // Ramp depth for Y-axis coordinate flip
    preferences?.heatmapHorizonHours || 72  // User-configurable time horizon
  );

  if (printable) {
    return (
      <Box>
        {props.children}
        {ramp?.version === "v1" ? (
          <RampCanvasReadOnly
            ramp={ramp}
            width={width}
            backgroundColor={rampBackgroundColor}
            showReferenceImage={showReferenceImage}
            showReferenceLines={false}
          />
        ) : (
          <RampCanvasReadOnlyNew
            ramp={ramp}
            width={width}
            showReferenceImage={showReferenceImage}
            showSpots={showSpots}
            zoomLevel={zoomLevel || 1}
          />
        )}
      </Box>
    );
  }

  return (
    <MeasuringTool pixelsToFeetRatio={feetToPixels}>
      <Box
        ref={ref}
        onClick={onClick}
        onContextMenu={onContextMenu}
        sx={{
          position: "relative",
          width,
          // height,
          height: "100%",
          marginBottom: "-7px",
          // TODO: i'm not sure why this was here, but it was causing the planes at the bottom of the ramp to be cut off
          // overflow: "hidden",
          "&:hover": selectable
            ? {
                border: "2px solid black",
                borderColor: "success.main",
                cursor: "pointer",
              }
            : {},
        }}
      >
        {props.children}
        {ramp?.version === "v1" ? (
          <RampCanvasReadOnly
            ramp={ramp}
            width={width}
            backgroundColor={rampBackgroundColor}
            showReferenceImage={showReferenceImage}
            showReferenceLines={false}
          />
        ) : (
          <RampCanvasReadOnlyNew
            ramp={ramp}
            width={width}
            showReferenceImage={showReferenceImage}
            showSpots={showSpots}
            zoomLevel={zoomLevel || 1}
          />
        )}
        {/* Zone Heatmap - rendered after background but before aircraft */}
        {showHeatmap && heatmapSelectedDate && ramp?.shapes && (
          <ZoneHeatmap
            shapes={ramp.shapes}
            occupancyData={heatmapData}
            width={width}
            height={height}
            pixelsToFeetRatio={feetToPixels}
            visible={showHeatmap}
          />
        )}
        {/* Temporarily adding a layer to draw on for debugging */}
        {/*<div 
          id="aircraftDebugDrawLayer"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            //right: 0,
            //bottom: 0,
            //backgroundColor: 'limegreen',
            width: width,
            height: width / ((ramp.width / (ramp.depth || 1)) || 1),
            pointerEvents: 'none',
          }}
        />*/}
      </Box>
    </MeasuringTool>
  );
});
